import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { copyFileToRelatedRequests } from '../../../../api'
import { EMPTY_LIST } from '../../../../constants/common'
import { useNotification } from '../../../../hooks'

/**
 * Duplicates one or more files from a request to all of its related requests.
 * Returns a `duplicate(fileIds)` callback. Resolves to the response on success,
 * `undefined` if validation fails (no requestId / empty fileIds).
 */
export function useDocumentDuplicateToRelated({ requestId, onReload }) {
  const { t } = useTranslation()

  const {
    handleAddErrorNotification,
    handleAddInfoNotification,
    handleAddSuccessNotification,
  } = useNotification()

  return useCallback(
    fileIds => {
      const filteredFileIds = (fileIds || EMPTY_LIST).filter(Boolean)

      if (!requestId || filteredFileIds.length === 0) {
        handleAddErrorNotification({
          title: t('Notification.DocumentDuplicateToRelatedErrorKey'),
        })

        return
      }

      handleAddInfoNotification(
        'Notification.DocumentDuplicateToRelatedStartKey'
      )

      return Promise.all(
        filteredFileIds.map(file =>
          copyFileToRelatedRequests(requestId, { file })
        )
      )
        .then(response => {
          handleAddSuccessNotification({
            title: t('Notification.DocumentDuplicateToRelatedSuccessKey'),
          })

          onReload?.()

          return response
        })
        .catch(error => {
          handleAddErrorNotification({
            title: t('Notification.DocumentDuplicateToRelatedErrorKey'),
          })

          throw error
        })
    },
    [
      handleAddErrorNotification,
      handleAddInfoNotification,
      handleAddSuccessNotification,
      onReload,
      requestId,
      t,
    ]
  )
}
